import styles from "./BlogPost.module.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Section } from "../util/section";
import format from "date-fns/format";
import { TinaMarkdown } from "tinacms/dist/rich-text";
import type { Components } from "tinacms/dist/rich-text";
import { tinaField } from "tinacms/dist/react";
import FreeTurnBoard from "./FreeTurnBoard";
import GameViewer from "./GameViewer";
import SimpleGameViewer from "./SimpleGameViewer";
import ArticleBoard from "./ArticleBoard";
import ChallengeBoard from "./ChallengeBoard";
import LessonBoard from "./LessonBoard";
import SvgViewer from "./SvgViewer";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import { cn } from "@/lib/utils";
import { getContentTemplate, getTopicHref } from "@/lib/contentRouting";
import OpeningReferenceDetails from "./OpeningReferenceDetails";

const READING_WORDS_PER_MINUTE = 250;

function countWordsInRichText(node: any): number {
  if (!node) {
    return 0;
  }

  if (Array.isArray(node)) {
    return node.reduce(
      (total, child) => total + countWordsInRichText(child),
      0,
    );
  }

  if (typeof node !== "object") {
    return 0;
  }

  return Object.entries(node).reduce((total, [key, value]) => {
    if (key === "text" && typeof value === "string") {
      const words = value.trim().split(/\s+/).filter(Boolean);
      return total + words.length;
    }

    return total + countWordsInRichText(value);
  }, 0);
}

export default function Post(props: any) {
  const [activeId, setActiveId] = useState(null);
  const [progress, setProgress] = useState("0%");
  const sectionRefs = useRef({});
  const articleRef = useRef<HTMLElement | null>(null);
  const { t } = useTranslation("common");

  const headings = props._body.children
    .filter(
      (node) =>
        node.children[0].text != null && node.type.substring(0, 2) == "h2",
    )
    .map((node) => {
      return {
        id: node.children[0].text.replace(/ /g, "-"),
        text: node.children[0].text,
      };
    });

  function findPrevSectionId(id) {
    const index = headings.findIndex((item) => item.id == id);
    return index > 0 ? headings[index - 1].id : id;
  }

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const observer = new IntersectionObserver(
      (entries) => {
        let nextActiveId: string | null = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting && window.scrollY >= lastScrollY) {
            nextActiveId = entry.target.id;
          } else if (
            !entry.isIntersecting &&
            window.scrollY < lastScrollY &&
            entry.target.id !== headings[0]?.id
          ) {
            const prevId = findPrevSectionId(entry.target.id);
            if (prevId) nextActiveId = prevId;
          }
        });
        if (nextActiveId) {
          setActiveId(nextActiveId);
        }
        lastScrollY = window.scrollY;
      },
      { rootMargin: "-20% 0px -68% 0px", threshold: [0, 0.5, 0.1] },
    );

    Object.values(sectionRefs.current).forEach((ref: Element) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const updateScrollProgress = () => {
      const article = articleRef.current;
      if (!article) {
        setProgress("0%");
        return;
      }

      const articleTop = article.getBoundingClientRect().top + window.scrollY;
      const readableDistance = Math.max(
        1,
        article.offsetHeight - window.innerHeight,
      );
      const percentage = Math.min(
        100,
        Math.max(0, ((window.scrollY - articleTop) / readableDistance) * 100),
      );

      setProgress(`${percentage}%`);
    };

    updateScrollProgress();
    window.addEventListener("scroll", updateScrollProgress);
    window.addEventListener("resize", updateScrollProgress);
    return () => {
      window.removeEventListener("scroll", updateScrollProgress);
      window.removeEventListener("resize", updateScrollProgress);
    };
  }, []);

  const components: Components<{
    FreeTurnBoard: any;
    DisplayBoard: any;
    ChallengeBoard: any;
    GameViewer: any;
    SimpleGameViewer: any;
    LessonBoard: any;
    SvgViewer: any;
  }> = {
    img: (props) => (
      <span className="flex items-center justify-start w-full overflow-x-visible">
        <img src={props.url} alt={props.alt} />
      </span>
    ),
    h2: (props) => {
      const text = props.children.props.content[0].text;
      const id = text.replace(/ /g, "-");

      return (
        <h2
          id={id}
          ref={(el) => {
            sectionRefs.current[id] = el;
          }}
        >
          {text}
        </h2>
      );
    },
    table: (props) => {
      const { children } = props as any;
      return (
        <div
          className={`not-prose my-8 rounded ring-1 ring-slate-200 ${styles.table}`}
        >
          <div className="overflow-hidden rounded">
            <table className="min-w-full text-sm border-separate border-spacing-0">
              {children}
            </table>
          </div>
        </div>
      );
    },
    FreeTurnBoard,
    DisplayBoard: ArticleBoard,
    ChallengeBoard,
    GameViewer,
    SimpleGameViewer,
    LessonBoard,
    SvgViewer,
  };

  const date = new Date(props.date);
  let formattedDate = "";
  if (!isNaN(date.getTime())) {
    formattedDate = format(date, "MMMM dd, yyyy");
  }
  const wordCount = useMemo(
    () => countWordsInRichText(props._body),
    [props._body],
  );
  const readTimeMinutes = Math.max(
    1,
    Math.ceil(wordCount / READING_WORDS_PER_MINUTE),
  );
  const categoryHref = getTopicHref(props?.category?.slug);
  const authorHref = props.author
    ? `/guides/author/${props.author._sys.filename}`
    : "/guides";
  const template = getContentTemplate(props);
  const displayTitle =
    template === "opening-reference" && props.opening?.name
      ? props.opening.name
      : props.title;
  const isNews =
    props?.category?.slug === "news" ||
    props?._sys?.breadcrumbs?.[0] === "news";

  // console.log("props._body", props._body);
  // console.log("props.author", props.author);

  return (
    <Section className={`flex-1 ${isNews ? "bg-white" : "bg-athens-gray-50"}`}>
      <div className="flex flex-col items-center">
        <div
          id="reading-progress-bar"
          className="bg-blue-600"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: progress,
            height: "5px",
            zIndex: "9999",
            transition: "width 0.3s ease-out",
          }}
        ></div>

        <div className="w-full max-w-[1360px] mx-auto px-6 py-10 sm:py-12 md:grid md:grid-cols-[180px_minmax(0,1fr)] md:gap-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8 xl:grid-cols-[240px_750px_minmax(0,1fr)] xl:gap-12">
          <aside className="hidden md:block md:col-start-1">
            <div className="sticky top-6">
              <p className="pl-3 mb-3 text-sm font-semibold text-altgrey-700">
                Contents
              </p>
              <nav className="space-y-1">
                {headings.map((heading) => {
                  return (
                    <Link
                      className={cn(
                        "block rounded px-3 py-1 text-sm transition-colors hover:text-gray-900",
                        activeId === heading.id
                          ? "bg-gray-200 text-blue-600"
                          : "text-gray-600",
                      )}
                      href={`#${heading.id}`}
                      key={heading.id}
                    >
                      {heading.text}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </aside>

          <article
            ref={articleRef}
            className="w-full max-w-[750px] mx-auto md:col-start-2 md:mx-0 xl:w-[750px] xl:max-w-none xl:justify-self-start"
          >
            <header className="mb-8">
              <Link
                data-tina-field={tinaField(props.category, "category")}
                className="inline-block mb-3 text-xs font-bold uppercase tracking-[0.18em] text-altblue-600 transition-colors hover:text-altblue-800"
                href={categoryHref}
                locale={false}
              >
                {t(props.category.category)}
              </Link>
              <h1
                data-tina-field={tinaField(props, "title")}
                className="text-3xl font-extrabold leading-tight tracking-normal text-left text-gray-900 title-font sm:text-4xl lg:text-[42px]"
              >
                {displayTitle}
              </h1>
              {props.subtitle && (
                <p
                  className="mt-3 text-xl leading-relaxed text-left text-slate-600 md:text-[22px]"
                  data-tina-field={tinaField(props, "subtitle")}
                >
                  {props.subtitle}
                </p>
              )}
              <div className="flex flex-wrap items-center mt-5 text-sm font-medium text-slate-600">
                {props.author?.name && (
                  <>
                    <span>
                      By{" "}
                      <Link
                        data-tina-field={tinaField(props.author, "name")}
                        className="text-gray-700 transition-colors hover:text-altblue-600"
                        href={authorHref}
                        locale={false}
                      >
                        {props.author.name}
                      </Link>
                    </span>
                    <span className="px-2" aria-hidden="true">
                      ·
                    </span>
                  </>
                )}
                {formattedDate && (
                  <>
                    <time
                      data-tina-field={tinaField(props, "date")}
                      dateTime={props.date}
                    >
                      {formattedDate}
                    </time>
                    <span className="px-2" aria-hidden="true">
                      ·
                    </span>
                  </>
                )}
                <span>{readTimeMinutes} min read</span>
              </div>
            </header>
            {props.cardImg ? (
              <div
                data-tina-field={tinaField(props, "cardImg")}
                className="relative mb-10 overflow-hidden rounded-2xl border border-slate-200 shadow-sm"
              >
                <img
                  src={props.cardImg}
                  alt=""
                  className="block h-auto w-full"
                />
              </div>
            ) : props.heroBoard?.fen ? (
              <div data-tina-field={tinaField(props, "heroBoard")}>
                <ArticleBoard
                  fen={props.heroBoard.fen}
                  playingWhite={props.heroBoard.playingWhite ?? true}
                  arrows={props.heroBoard.arrows || []}
                  highlights={props.heroBoard.highlights || []}
                  caption={props.heroBoard.caption}
                  presentation="hero"
                />
              </div>
            ) : props.heroImg ? (
              <div
                data-tina-field={tinaField(props, "heroImg")}
                className="relative mb-10"
              >
                <img
                  src={props.heroImg}
                  alt={props.title}
                  className="block w-full h-auto rounded-lg"
                />
              </div>
            ) : null}
            {template === "opening-reference" && (
              <OpeningReferenceDetails opening={props.opening} />
            )}
            <div
              data-tina-field={tinaField(props, "_body")}
              className="w-full prose prose-slate max-w-none dark:prose-dark"
            >
              <TinaMarkdown components={components} content={props._body} />

              <div className={"h-96 "}> </div>
            </div>
          </article>
        </div>
      </div>
    </Section>
  );
}
